import type { ReactNode } from 'react';
import { Check, SlidersHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ToneDot } from '@/components/views/atoms/ToneDot';
import type { AppLabel } from '@/lib/app-collaboration';
import type { AppStatus } from '@/lib/app-records';
import { type AppListQuery, EMPTY_APP_LIST_QUERY } from './app-records-model';
import { usePopover } from './usePopover';

interface AppListFiltersProps {
  query: AppListQuery;
  onChange: (query: AppListQuery) => void;
  statuses: AppStatus[];
  counts: Record<string, number>;
  labels: AppLabel[];
  /** Objects with a next action; null when the app has no work Thread. */
  nextActionCount?: number | null;
}

export const activeFilterCount = (query: AppListQuery) =>
  [query.status, query.label].filter(Boolean).length + [query.mine, query.hideDone, query.archived, query.withNextAction].filter(Boolean).length;

const Item = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) => (
  <button type="button" role="menuitemcheckbox" aria-checked={active} onClick={onClick}
    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-domino-ink hover:bg-domino-muted">
    <Check className={cn('h-3.5 w-3.5 shrink-0 text-domino-aubergine', !active && 'invisible')} aria-hidden="true" />
    {children}
  </button>
);

const Heading = ({ children }: { children: string }) => (
  <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-domino-inkSoft">{children}</p>
);

/** The list's filters (status, label, mine, done, archives) behind one button. */
export const AppListFilters = ({ query, onChange, statuses, counts, labels, nextActionCount = null }: AppListFiltersProps) => {
  const { open, setOpen, ref: root } = usePopover();
  const active = activeFilterCount(query);

  return (
    <div ref={root} className="relative">
      <button type="button" aria-expanded={open} aria-haspopup="menu" onClick={() => setOpen(!open)}
        className={cn('inline-flex items-center gap-2 rounded-lg border bg-domino-white px-3 py-1.5 text-sm text-domino-ink transition hover:border-domino-aubergine',
          active > 0 ? 'border-domino-aubergine' : 'border-domino-cardBorder')}>
        <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
        Filtres
        {active > 0 && <span className="rounded-full bg-domino-aubergine px-1.5 text-xs text-white">{active}</span>}
      </button>
      {open && (
        <div role="menu" className="absolute left-0 top-10 z-20 w-64 overflow-hidden rounded-2xl border border-domino-cardBorder bg-domino-white py-1 shadow-[var(--domino-shadow-elevated)]">
          <Heading>Statut</Heading>
          {statuses.map((status) => (
            <Item key={status.key} active={query.status === status.key}
              onClick={() => onChange({ ...query, status: query.status === status.key ? null : status.key })}>
              <ToneDot tone={status.tone} />
              <span className="flex-1">{status.label}</span>
              <span className="text-xs text-domino-inkSoft">{counts[status.key] ?? 0}</span>
            </Item>
          ))}
          {labels.length > 0 && <>
            <Heading>Étiquette</Heading>
            {labels.map((label) => (
              <Item key={label.id} active={query.label === label.id}
                onClick={() => onChange({ ...query, label: query.label === label.id ? null : label.id })}>
                <ToneDot tone={label.tone} />{label.name}
              </Item>
            ))}
          </>}
          <div className="my-1 border-t border-domino-cardBorder" />
          {nextActionCount !== null && (
            <Item active={query.withNextAction} onClick={() => onChange({ ...query, withNextAction: !query.withNextAction })}>
              <span className="flex-1">Avec une prochaine action</span>
              <span className="text-xs text-domino-inkSoft">{nextActionCount}</span>
            </Item>
          )}
          <Item active={query.mine} onClick={() => onChange({ ...query, mine: !query.mine })}>Assignées à moi</Item>
          <Item active={query.hideDone} onClick={() => onChange({ ...query, hideDone: !query.hideDone })}>Masquer les faites</Item>
          <Item active={query.archived} onClick={() => onChange({ ...query, archived: !query.archived })}>Archives</Item>
          {active > 0 && <>
            <div className="my-1 border-t border-domino-cardBorder" />
            <button type="button" onClick={() => onChange({ ...EMPTY_APP_LIST_QUERY, search: query.search })}
              className="w-full px-3 py-1.5 text-left text-sm text-domino-aubergine hover:bg-domino-muted">Effacer les filtres</button>
          </>}
        </div>
      )}
    </div>
  );
};
